import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Link2, Plus, Trash2, Search, Loader2, AlertCircle, CheckCircle,
  Building2, Hotel, X, ArrowRight, RefreshCw
} from 'lucide-react';
import { propertyMappingAPI } from '../../services/api';

const PropertyMappingSettings = () => {
  const [mappings, setMappings] = useState([]);
  const [crm, setCrm] = useState([]);
  const [recco, setRecco] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selection state
  const [selectedCrm, setSelectedCrm] = useState(null);
  const [selectedRecco, setSelectedRecco] = useState(null);
  const [crmSearch, setCrmSearch] = useState('');
  const [reccoSearch, setReccoSearch] = useState('');
  const [mappingSearch, setMappingSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mappingsRes, availableRes] = await Promise.all([
        propertyMappingAPI.getAll(),
        propertyMappingAPI.getAvailable(),
      ]);
      setMappings(mappingsRes.data.mappings || []);
      setCrm(availableRes.data.crm_properties || []);
      setRecco(availableRes.data.recco_properties || []);
    } catch (err) {
      setError('Failed to load data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredCrm = useMemo(() => {
    if (!crmSearch) return crm.slice(0, 50);
    const q = crmSearch.toLowerCase();
    return crm.filter(c => c.name.toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q)).slice(0, 50);
  }, [crm, crmSearch]);

  const filteredRecco = useMemo(() => {
    if (!reccoSearch) return recco.slice(0, 50);
    const q = reccoSearch.toLowerCase();
    return recco.filter(r => r.name.toLowerCase().includes(q) || (r.city || '').toLowerCase().includes(q)).slice(0, 50);
  }, [recco, reccoSearch]);

  const filteredMappings = useMemo(() => {
    if (!mappingSearch) return mappings;
    const q = mappingSearch.toLowerCase();
    return mappings.filter(m =>
      m.crm_property_name.toLowerCase().includes(q) ||
      m.recco_property_name.toLowerCase().includes(q)
    );
  }, [mappings, mappingSearch]);

  const handleMap = async () => {
    if (!selectedCrm || !selectedRecco) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await propertyMappingAPI.create({
        crm_lead_id: selectedCrm.id,
        crm_property_name: selectedCrm.name,
        recco_property_id: selectedRecco.id,
        recco_property_name: selectedRecco.name,
      });
      setSuccess(`Mapped "${selectedCrm.name}" with "${selectedRecco.name}"`);
      setSelectedCrm(null);
      setSelectedRecco(null);
      setCrmSearch('');
      setReccoSearch('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create mapping');
    }
    setSaving(false);
  };

  const handleDelete = async (mapping) => {
    setDeleting(p => ({ ...p, [mapping.id]: true }));
    setError('');
    setSuccess('');
    try {
      await propertyMappingAPI.delete(mapping.id);
      setSuccess(`Removed mapping for "${mapping.crm_property_name}"`);
      await loadData();
    } catch (err) {
      setError('Failed to remove mapping');
    }
    setDeleting(p => ({ ...p, [mapping.id]: false }));
  };

  // Auto-clear messages
  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(''); setError(''); }, 4000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="property-mapping-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Link2 size={20} className="text-teal-500" />
            Property Mapping
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Link CRM properties (Hotel Onboarded) with Recco properties for data exchange</p>
        </div>
        <button onClick={loadData} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg" data-testid="mapping-refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="mapping-error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700" data-testid="mapping-success">
          <CheckCircle size={16} /> {success}
        </div>
      )}

      {/* Create Mapping Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800">Create Mapping</h3>
          <p className="text-xs text-gray-500 mt-0.5">Select one CRM property and one Recco property to link them</p>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-start">
            {/* CRM Property Selector */}
            <div data-testid="crm-selector">
              <label className="block text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <Building2 size={14} className="text-teal-500" />
                CRM Property (Hotel Onboarded)
                <span className="text-gray-400 font-normal ml-auto">{crm.length} available</span>
              </label>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={crmSearch} onChange={e => setCrmSearch(e.target.value)}
                  placeholder="Search CRM properties..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid="crm-search" />
              </div>
              {selectedCrm ? (
                <div className="flex items-center justify-between px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-lg" data-testid="crm-selected">
                  <div>
                    <p className="text-sm font-semibold text-teal-800">{selectedCrm.name}</p>
                    <p className="text-[10px] text-teal-600">{[selectedCrm.city, selectedCrm.state].filter(Boolean).join(', ')}</p>
                  </div>
                  <button onClick={() => setSelectedCrm(null)} className="p-1 hover:bg-teal-100 rounded"><X size={14} className="text-teal-500" /></button>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto" data-testid="crm-dropdown">
                  {filteredCrm.length > 0 ? filteredCrm.map(c => (
                    <button key={c.id} onClick={() => setSelectedCrm(c)}
                      className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b border-gray-50 last:border-0 transition-colors"
                      data-testid={`crm-option-${c.id.slice(0, 8)}`}>
                      <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                      <p className="text-[10px] text-gray-500">{[c.city, c.state].filter(Boolean).join(', ')}</p>
                    </button>
                  )) : (
                    <p className="p-4 text-center text-xs text-gray-400">No unmapped CRM properties found</p>
                  )}
                </div>
              )}
            </div>

            {/* Arrow */}
            <div className="hidden lg:flex items-center justify-center pt-8">
              <div className="w-10 h-10 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center">
                <ArrowRight size={16} className="text-teal-500" />
              </div>
            </div>

            {/* Recco Property Selector */}
            <div data-testid="recco-selector">
              <label className="block text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <Hotel size={14} className="text-blue-500" />
                Recco Property (Properties)
                <span className="text-gray-400 font-normal ml-auto">{recco.length} available</span>
              </label>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={reccoSearch} onChange={e => setReccoSearch(e.target.value)}
                  placeholder="Search Recco properties..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                  data-testid="recco-search" />
              </div>
              {selectedRecco ? (
                <div className="flex items-center justify-between px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg" data-testid="recco-selected">
                  <div>
                    <p className="text-sm font-semibold text-blue-800">{selectedRecco.name}</p>
                    <p className="text-[10px] text-blue-600">{[selectedRecco.city, selectedRecco.state].filter(Boolean).join(', ')}</p>
                  </div>
                  <button onClick={() => setSelectedRecco(null)} className="p-1 hover:bg-blue-100 rounded"><X size={14} className="text-blue-500" /></button>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto" data-testid="recco-dropdown">
                  {filteredRecco.length > 0 ? filteredRecco.map(r => (
                    <button key={r.id} onClick={() => setSelectedRecco(r)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-50 last:border-0 transition-colors"
                      data-testid={`recco-option-${r.id.slice(0, 8)}`}>
                      <p className="text-sm font-medium text-gray-800 truncate">{r.name}</p>
                      <p className="text-[10px] text-gray-500">{[r.city, r.state].filter(Boolean).join(', ')}</p>
                    </button>
                  )) : (
                    <p className="p-4 text-center text-xs text-gray-400">No unmapped Recco properties found</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Map Button */}
          <div className="mt-5 flex justify-center">
            <button
              onClick={handleMap}
              disabled={!selectedCrm || !selectedRecco || saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="map-property-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              Map Property
            </button>
          </div>
        </div>
      </div>

      {/* Mapped Records Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Mapped Properties</h3>
            <p className="text-xs text-gray-500">{mappings.length} mapping{mappings.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="relative w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={mappingSearch} onChange={e => setMappingSearch(e.target.value)}
              placeholder="Search mappings..."
              className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-teal-400 outline-none"
              data-testid="mapping-search" />
          </div>
        </div>
        {filteredMappings.length > 0 ? (
          <div className="overflow-x-auto" data-testid="mapping-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">CRM Property</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 w-12">
                    <Link2 size={14} className="mx-auto" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Recco Property</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Mapped On</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredMappings.map(m => (
                  <tr key={m.id} className="hover:bg-gray-50 transition-colors" data-testid={`mapping-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-teal-500 shrink-0" />
                        <span className="font-medium text-gray-800">{m.crm_property_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <ArrowRight size={14} className="text-gray-300 mx-auto" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Hotel size={14} className="text-blue-500 shrink-0" />
                        <span className="font-medium text-gray-800">{m.recco_property_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(m.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(m)}
                        disabled={deleting[m.id]}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                        data-testid={`mapping-delete-${m.id}`}
                        title="Remove Mapping"
                      >
                        {deleting[m.id] ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <Link2 size={36} className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">No Mappings Yet</h3>
            <p className="text-sm text-gray-500">Create your first mapping above to link CRM and Recco properties</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyMappingSettings;
